/**
 * Date-related helper utilities for discipline service
 */

import { schoolDatePeriodsService } from '../../schoolDatePeriodsService.js';
import { doc, getDocFromServer, getDocsFromServer } from 'firebase/firestore';
import { db } from '../../../firebase.js';
import { SchoolRulePeriodError, isSchoolRulePeriodError, needsSchoolRulePeriod, normalizeRuleTimeframe, resolveSchoolRulePeriod, schoolRuleTimeZone } from '../../domain/schoolRulePeriods.js';
export { isSchoolRulePeriodError } from '../../domain/schoolRulePeriods.js';

function zonedParts(instant, timeZone) {
  return Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(instant)
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, Number(part.value)])
  );
}

function localDateTimeToInstant(parts, timeZone) {
  const desiredUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour || 0,
    parts.minute || 0,
    parts.second || 0,
    parts.millisecond || 0
  );
  let candidate = new Date(desiredUtc);
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const actual = zonedParts(candidate, timeZone);
    const representedUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second
    );
    const adjustment = desiredUtc - representedUtc;
    if (adjustment === 0) break;
    candidate = new Date(candidate.getTime() + adjustment);
  }
  return candidate;
}

function getZonedDayRange(referenceDate, timeCount, timeZone) {
  const local = zonedParts(referenceDate, timeZone);
  const startDay = new Date(Date.UTC(
    local.year,
    local.month - 1,
    local.day - (timeCount - 1)
  ));
  const nextDay = new Date(Date.UTC(local.year, local.month - 1, local.day + 1));
  const fromUtcDate = date => ({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate()
  });
  const start = localDateTimeToInstant(fromUtcDate(startDay), timeZone);
  const nextStart = localDateTimeToInstant(fromUtcDate(nextDay), timeZone);
  return { start, end: new Date(nextStart.getTime() - 1) };
}

/**
 * Get date range for a specific timeframe
 * @param {string} timeframe - The timeframe (day, week, month, term, semester, year)
 * @param {number} timeCount - Number of timeframes to go back
 * @param {string} schoolId - The school ID (required for custom period support)
 * @returns {Promise<Object>} Object with start and end dates
 */
export async function getDateRangeForTimeframe(timeframe, timeCount = 1, schoolId = null, referenceDate = new Date(), timeZone = null) {
  const type = normalizeRuleTimeframe(timeframe);
  if (!schoolId) throw new SchoolRulePeriodError('A school is required to read its Time Periods.');
  try {
    // Fresh reads include older records without sort metadata. Display ordering
    // and the UI cache must not decide which saved period a rule can see.
    const [snapshot, school] = await Promise.all([
      getDocsFromServer(schoolDatePeriodsService.getCollectionRef(schoolId)),
      getDocFromServer(doc(db, 'schools', schoolId)),
    ]);
    const schoolZone = schoolRuleTimeZone(school.data() || {}, timeZone || 'Africa/Johannesburg');
    const periods = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    const year = resolveSchoolRulePeriod(periods, 'year', referenceDate, schoolZone);
    const range = needsSchoolRulePeriod(type)
      ? type === 'year' ? year : resolveSchoolRulePeriod(periods, type, referenceDate, schoolZone)
      : getDateRangeForTimeframeSync(type, timeCount, referenceDate, schoolZone);
    const saved = period => ({periodId: period.periodId, periodType: period.periodType,
      start: period.start.toISOString(), end: period.end.toISOString()});
    return {...range, timeZone: schoolZone,
      start: new Date(Math.max(year.start.getTime(), range.start.getTime())),
      end: new Date(Math.min(year.end.getTime(), range.end.getTime())),
      rulePeriods: [saved(year), ...(range.periodId && range.periodId !== year.periodId ? [saved(range)] : [])]};
  } catch (error) {
    if (isSchoolRulePeriodError(error)) throw error;
    throw new SchoolRulePeriodError(`Could not read ${type} dates from Time Periods. Retry after the school configuration is available.`);
  }
}

/**
 * Synchronous version of getDateRangeForTimeframe (for backward compatibility)
 * @param {string} timeframe - The timeframe (day, week, month, term, semester, year)
 * @param {number} timeCount - Number of timeframes to go back
 * @param {Date|string|Object} referenceDate - Date that anchors the rule window
 * @param {string|null} timeZone - Optional IANA timezone for calendar-day rules
 * @returns {Object} Object with start and end dates
 */
export function getDateRangeForTimeframeSync(
  timeframe,
  timeCount = 1,
  referenceDate = new Date(),
  timeZone = null
) {
  timeframe = normalizeRuleTimeframe(timeframe);
  if (needsSchoolRulePeriod(timeframe)) throw new SchoolRulePeriodError('Read the school’s saved Time Periods before evaluating this rule.');
  const resolvedReference = referenceDate?.toDate?.() || new Date(referenceDate);
  const validReference = Number.isNaN(resolvedReference.getTime()) ? new Date() : resolvedReference;
  if (timeframe === 'day' && timeZone) {
    return getZonedDayRange(validReference, timeCount, timeZone);
  }
  const end = new Date(validReference);
  const start = new Date(validReference);

  switch (timeframe) {
    case 'day':
      // Use calendar day boundaries instead of rolling 24-hour window
      start.setHours(0, 0, 0, 0); // Start of today
      start.setDate(start.getDate() - (timeCount - 1)); // Go back N-1 days
      end.setHours(23, 59, 59, 999); // End of today
      break;
    case 'week': {
      // Use calendar week boundaries (Monday to Sunday)
      const currentDay = end.getDay(); // 0 (Sun) to 6 (Sat)
      const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1; // Adjust so Monday = 0

      // Start of current week (Monday at 00:00:00)
      start.setDate(end.getDate() - daysFromMonday - ((timeCount - 1) * 7));
      start.setHours(0, 0, 0, 0);

      // End of current week (Sunday at 23:59:59)
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      break;
    }
    case 'month':
      // Use calendar month boundaries (1st to last day of month)
      start.setDate(1); // First day of current month
      start.setMonth(end.getMonth() - (timeCount - 1));
      start.setHours(0, 0, 0, 0);

      // Last day of current month at 23:59:59
      end.setMonth(end.getMonth() + 1, 0); // 0th day of next month = last day of current month
      end.setHours(23, 59, 59, 999);
      break;
    default:
      start.setDate(end.getDate() - (timeCount * 7));
  }

  return { start, end };
}

/**
 * Get current active periods for a school
 * @param {string} schoolId - The school ID
 * @returns {Promise<Object>} Object with active periods by type
 */
export async function getActivePeriods(schoolId) {
  if (!schoolId) return {};
  
  try {
    const activePeriods = await schoolDatePeriodsService.getActivePeriods(schoolId);
    const result = {};
    
    // Group active periods by type
    activePeriods.forEach(period => {
      result[period.type] = period;
    });
    
    return result;
  } catch (error) {
    console.warn('Error getting active periods:', error);
    return {};
  }
}

/**
 * Check if two timestamps represent the same lesson
 * Considers same lesson if within 2 hours and same day
 * @param {Date|Object} timestamp1 - First timestamp (Date or Firestore Timestamp)
 * @param {Date|Object} timestamp2 - Second timestamp (Date or Firestore Timestamp)
 * @returns {boolean} True if timestamps are in the same lesson
 */
export function isSameLesson(timestamp1, timestamp2) {
  const date1 = timestamp1?.toDate?.() || timestamp1;
  const date2 = timestamp2?.toDate?.() || timestamp2;

  // Consider same lesson if within 2 hours and same day
  const timeDiff = Math.abs(date1.getTime() - date2.getTime());
  const twoHours = 2 * 60 * 60 * 1000;
  const sameDay = date1.toDateString() === date2.toDateString();

  return sameDay && timeDiff <= twoHours;
}
